import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_CURRENCY,
  US_STATES,
  createJobInputSchema,
  formatMoney,
  toMinorUnits,
  type CreateJobInput,
} from '@teentack/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createJob } from '@/features/jobs/jobs.api';
import { jobKeys } from '@/features/jobs/use-jobs';
import { useSkills } from '@/hooks/use-skills';
import { PosterShell } from '@/features/poster/PosterShell';
import { ErrorNote } from '@/components/Form';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput, Textarea } from '@/components/ui/Input';
import { ChoiceChip } from '@/components/signup/ChoiceChip';
import { presentApiError } from '@/lib/form-errors';

const CATEGORIES = [
  'Care',
  'Household',
  'Outdoor',
  'Tutoring',
  'Tech',
  'Creative',
  'Food & hospitality',
  'Retail',
  'Sports & activity',
  'Office & admin',
  'Community',
];

const EMPTY = {
  title: '',
  description: '',
  category: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  payAmount: '',
  teensNeeded: '1',
  instructions: '',
};

/**
 * Create and publish a job. [Job Poster steps 5-10]
 *
 * Pay is typed in major units because that is how a person thinks about money,
 * and converted to integer minor units on the way out — `toMinorUnits` works on
 * the string so `45.50` never becomes `4550.000000000001`.
 */
export function PostJobPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const skillsQuery = useSkills();

  const [fields, setFields] = useState(EMPTY);
  const [needType, setNeedType] = useState<'SINGLE_TEEN' | 'MULTIPLE_TEENS'>('SINGLE_TEEN');
  const [payType, setPayType] = useState<'FIXED' | 'BID'>('FIXED');
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CreateJobInput) => createJob(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });

  const set =
    (key: keyof typeof fields) =>
    (event: { target: { value: string } }): void => {
      setFields((current) => ({ ...current, [key]: event.target.value }));
      setFieldErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
    };

  /** Live preview of what the teen will see, so the number is never a surprise. */
  function previewPay(): string | null {
    if (!fields.payAmount.trim()) return null;
    try {
      return formatMoney(toMinorUnits(fields.payAmount), DEFAULT_CURRENCY);
    } catch {
      return null;
    }
  }

  async function submit(status: 'DRAFT' | 'LIVE'): Promise<void> {
    setError(null);
    setFieldErrors({});

    let payAmountMinor: number;
    try {
      payAmountMinor = toMinorUnits(fields.payAmount);
    } catch {
      setFieldErrors({ payAmount: 'Enter an amount like 45 or 45.50' });
      return;
    }

    const candidate = {
      title: fields.title.trim(),
      description: fields.description.trim(),
      category: fields.category,
      ...(fields.streetAddress.trim() ? { streetAddress: fields.streetAddress.trim() } : {}),
      ...(fields.city.trim() ? { city: fields.city.trim() } : {}),
      ...(fields.state ? { state: fields.state } : {}),
      ...(fields.zipCode.trim() ? { zipCode: fields.zipCode.trim() } : {}),
      country: 'United States',
      ...(skillIds.length > 0 ? { skillIds } : {}),
      needType,
      teensNeeded: needType === 'MULTIPLE_TEENS' ? Number(fields.teensNeeded) : 1,
      payType,
      payAmountMinor,
      currency: DEFAULT_CURRENCY,
      // Held back until someone is assigned, so an address is not published to
      // everyone browsing. [Job Poster step 22]
      ...(fields.instructions.trim()
        ? { instructions: { notes: fields.instructions.trim() } }
        : {}),
      status,
    };

    // Validated with the same schema the api uses, before the round trip.
    const parsed = createJobInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const issues: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0] ?? '');
        if (field && !issues[field]) issues[field] = issue.message;
      }
      setFieldErrors(issues);
      setError(parsed.error.issues[0]?.message ?? 'Please check the form');
      return;
    }

    try {
      const job = await mutation.mutateAsync(parsed.data);
      navigate(`/jobs/${job.id}`, { state: { justCreated: true } });
    } catch (caught) {
      const presented = presentApiError(caught, 'Could not save the job');
      setFieldErrors(presented.fields);
      setError(presented.message);
    }
  }

  const busy = mutation.isPending;
  const pay = previewPay();

  return (
    <PosterShell
      title="Post a job"
      description="Teens see this once you publish. Save it as a draft first if you are not ready."
      action={<span />}
    >
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void submit('LIVE');
        }}
        noValidate
        className="space-y-6"
      >
        <ErrorNote message={error} />

        <section className="space-y-5 rounded-2xl border border-line bg-white p-6 shadow-soft">
          <h2 className="text-base font-bold text-ink">The work</h2>

          <Input
            label="Title"
            value={fields.title}
            onChange={set('title')}
            error={fieldErrors.title}
            placeholder="Saturday dog walking"
            maxLength={200}
            required
          />

          <Textarea
            label="What needs doing?"
            value={fields.description}
            onChange={set('description')}
            error={fieldErrors.description}
            maxLength={5000}
            placeholder="Two friendly labradors, 9am to 11am. Leads and bags provided."
            hint={`${fields.description.length}/5000 characters`}
            required
          />

          <Textarea
            label="Instructions for whoever takes it"
            value={fields.instructions}
            onChange={set('instructions')}
            maxLength={2000}
            optional
            placeholder="Side gate code is 4821. Gloves are in the shed."
            hint="Only shown to a teen once you assign them — not on the public listing."
          />

          <SelectInput
            label="Category"
            value={fields.category}
            onChange={set('category')}
            error={fieldErrors.category}
            required
          >
            <option value="">Choose a category</option>
            {CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </SelectInput>

          <div>
            <p className="mb-2.5 flex items-baseline justify-between text-sm font-semibold text-ink">
              <span>Skills you need</span>
              <span className="text-xs font-medium text-slate-400">Optional</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {(skillsQuery.data ?? []).slice(0, 24).map((skill) => (
                <ChoiceChip
                  key={skill.publicId}
                  selected={skillIds.includes(skill.publicId)}
                  onToggle={() =>
                    setSkillIds((current) =>
                      current.includes(skill.publicId)
                        ? current.filter((id) => id !== skill.publicId)
                        : [...current, skill.publicId],
                    )
                  }
                >
                  {skill.name}
                </ChoiceChip>
              ))}
            </div>
          </div>
        </section>

        <section className="space-y-5 rounded-2xl border border-line bg-white p-6 shadow-soft">
          <h2 className="text-base font-bold text-ink">Where</h2>

          <Input
            label="Street address"
            value={fields.streetAddress}
            onChange={set('streetAddress')}
            error={fieldErrors.streetAddress}
            optional
            autoComplete="street-address"
            hint="Only shared with the teen once you assign them."
          />

          <div className="grid gap-4 sm:grid-cols-[1.4fr_1fr_1fr]">
            <Input
              label="City"
              value={fields.city}
              onChange={set('city')}
              error={fieldErrors.city}
              optional
            />
            <SelectInput
              label="State"
              value={fields.state}
              onChange={set('state')}
              error={fieldErrors.state}
              optional
            >
              <option value="">Select</option>
              {US_STATES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name}
                </option>
              ))}
            </SelectInput>
            <Input
              label="ZIP"
              value={fields.zipCode}
              onChange={set('zipCode')}
              error={fieldErrors.zipCode}
              optional
              inputMode="numeric"
              placeholder="78701"
            />
          </div>
        </section>

        <section className="space-y-5 rounded-2xl border border-line bg-white p-6 shadow-soft">
          <h2 className="text-base font-bold text-ink">Teens and pay</h2>

          <div>
            <p className="mb-2.5 text-sm font-semibold text-ink">How many teens?</p>
            <div className="flex flex-wrap gap-2">
              <ChoiceChip
                selected={needType === 'SINGLE_TEEN'}
                onToggle={() => setNeedType('SINGLE_TEEN')}
              >
                Just one
              </ChoiceChip>
              <ChoiceChip
                selected={needType === 'MULTIPLE_TEENS'}
                onToggle={() => setNeedType('MULTIPLE_TEENS')}
              >
                Several
              </ChoiceChip>
            </div>
          </div>

          {needType === 'MULTIPLE_TEENS' && (
            <Input
              label="How many do you need?"
              type="number"
              min={2}
              max={50}
              value={fields.teensNeeded}
              onChange={set('teensNeeded')}
              error={fieldErrors.teensNeeded}
              hint="They all work the same time and place."
              required
            />
          )}

          <div>
            <p className="mb-2.5 text-sm font-semibold text-ink">How is it paid?</p>
            <div className="flex flex-wrap gap-2">
              <ChoiceChip selected={payType === 'FIXED'} onToggle={() => setPayType('FIXED')}>
                A fixed amount
              </ChoiceChip>
              <ChoiceChip selected={payType === 'BID'} onToggle={() => setPayType('BID')}>
                Open to bids
              </ChoiceChip>
            </div>
          </div>

          <Input
            label={payType === 'BID' ? 'Your budget per teen' : 'Pay per teen'}
            value={fields.payAmount}
            onChange={set('payAmount')}
            error={fieldErrors.payAmount ?? fieldErrors.payAmountMinor}
            inputMode="decimal"
            placeholder="45.00"
            hint={pay ? `Teens will see ${pay}` : 'In US dollars, e.g. 45 or 45.50'}
            required
          />
        </section>

        <div className="sticky bottom-4 flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white/95 p-4 shadow-lift backdrop-blur">
          <Button type="submit" disabled={busy} aria-busy={busy || undefined} className="flex-1">
            {busy ? 'Saving…' : 'Publish job'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => void submit('DRAFT')}
          >
            Save as draft
          </Button>
        </div>
      </form>
    </PosterShell>
  );
}
